import { z, ZodType } from 'zod';

export const SETTING_TYPES = {
	string: 'string',
	number: 'number',
	array: 'array',
} as const satisfies Record<string, string>;

export type SettingType = (typeof SETTING_TYPES)[keyof typeof SETTING_TYPES];

export type SettingValues = {
	[SETTING_TYPES.string]: string;
	[SETTING_TYPES.number]: number;
	[SETTING_TYPES.array]: number[];
};

export type SettingBase<TType extends SettingType> = {
	code: string;
	value: SettingValues[TType];
	type: TType;
};

export type StringSetting = SettingBase<typeof SETTING_TYPES.string>;

export type NumberSetting = SettingBase<typeof SETTING_TYPES.number>;

export type NumberArraySetting = SettingBase<typeof SETTING_TYPES.array>;

export type Setting = StringSetting | NumberSetting | NumberArraySetting;

export const createSettingSchema = <TSetting extends Setting>(
	type: TSetting['type'],
	valueSchema: ZodType<TSetting['value']>,
) =>
	z.object({
		code: z.string(),
		value: valueSchema,
		type: z.literal(type),
	}) as ZodType<TSetting>;

export const stringSettingSchema = createSettingSchema<StringSetting>(
	SETTING_TYPES.string,
	z.string(),
);

export const numberSettingSchema = createSettingSchema<NumberSetting>(
	SETTING_TYPES.number,
	z.number(),
);

export const numberArraySettingSchema = createSettingSchema<NumberArraySetting>(
	SETTING_TYPES.array,
	z.number().array(),
);

export const settingSchema: ZodType<Setting> = z.union([
	stringSettingSchema,
	numberSettingSchema,
	numberArraySettingSchema,
]);
